import { Button, Col, DatePicker, Input, Row, Select } from 'antd';
import dayjs from 'dayjs';
import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AddSvg, SearchSvg } from 'utils/svgs';
import { checkPermission } from 'utils/utils';
import styles from './index.module.scss';
import { SearchAndFilterProps } from './types';

const { RangePicker } = DatePicker;

const selectNarrow = { width: 108 };

const SearchAndFilter: FC<SearchAndFilterProps> = (props) => {
  const {
    performSearchHandler,
    searchValue,
    openDrawerHandler,
    title,
    setSearch,
    totalRecords,
    statusFilter,
    onStatusFilterChange,
    selectedDate,
    setSelectedDate,
    allUsers,
    selectedUser,
    setSelectedUser,
  } = props;

  const userPermissions = useSelector((state: any) => state?.userProfile?.data);
  const isGroceryQcViewAll = checkPermission(userPermissions?.permissions, {
    permissionName: 'GroceryQualityCheck',
    permission: ['viewAll'],
  });
  const showStatusFilter =
    userPermissions?.isAdmin === true || isGroceryQcViewAll;

  const isGroceryItemsUserFilter = checkPermission(userPermissions?.permissions, {
    permissionName: 'GroceryItemsUserFilter',
    permission: ['view'],
  });
  const canUseUserFilter =
    userPermissions?.isAdmin === true || isGroceryItemsUserFilter;

  const isAddPermission = checkPermission(userPermissions?.permissions, {
    permissionName: 'GroceryItems',
    permission: ['add'],
  });

  const userOptions = useMemo(
    () =>
      (allUsers || []).map((item) => ({
        label: item?.name,
        value: item?.userId,
      })),
    [allUsers],
  );

  return (
    <div className={styles['search-filter']}>
      <div className={styles['search-filter__wrapper']}>
        <Row className={styles['search-filter__header']} align="middle" wrap={false}>
          <Col flex="none">
            <div className={styles['search-filter__title']}>
              {title}
              <span className={styles['search-filter__title--count']}>
                ({totalRecords})
              </span>
            </div>
          </Col>
          <Col flex="auto" style={{ minWidth: 0 }}>
            <div className={styles['search-filter__toolbar']}>
              <Button
                size="middle"
                className={styles['search-filter__clearBtn']}
                onClick={() => {
                  setSearch('');
                  if (showStatusFilter) {
                    onStatusFilterChange?.(null);
                  }
                  if (canUseUserFilter) {
                    setSelectedUser?.(null);
                  }
                  setSelectedDate?.(null);
                }}
              >
                Clear all
              </Button>

              <Input
                className={styles['search-filter__search']}
                placeholder="Search product..."
                prefix={<SearchSvg />}
                onChange={performSearchHandler}
                value={searchValue}
                size="middle"
                allowClear
              />

              {showStatusFilter && (
                <Select
                  style={selectNarrow}
                  value={statusFilter ?? undefined}
                  placeholder="Status"
                  size="middle"
                  onChange={(value) => onStatusFilterChange?.(value ?? null)}
                  allowClear
                  options={[
                    { label: 'Pending', value: 'PENDING' },
                    { label: 'Rejected', value: 'REJECTED' },
                    { label: 'Approved', value: 'APPROVED' },
                  ]}
                />
              )}

              {canUseUserFilter && (
                <Select
                  className={styles['search-filter__userSelect']}
                  showSearch
                  optionFilterProp="label"
                  popupMatchSelectWidth={false}
                  dropdownStyle={{ minWidth: 220 }}
                  popupClassName="grocery-user-select-dropdown"
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  value={selectedUser ?? undefined}
                  placeholder="Select User"
                  size="middle"
                  onChange={(value) => setSelectedUser?.(value ?? null)}
                  allowClear
                  options={userOptions}
                />
              )}

              <RangePicker
                className={styles['search-filter__dateRange']}
                picker="date"
                format="DD/MM/YY"
                size="middle"
                allowClear
                onChange={(value) => {
                  if (!value || !value[0] || !value[1]) {
                    setSelectedDate?.(null);
                  } else {
                    setSelectedDate?.([
                      dayjs(value[0]).startOf('day'),
                      dayjs(value[1]).endOf('day'),
                    ]);
                  }
                }}
                value={selectedDate}
              />

              {isAddPermission && openDrawerHandler && (
                <div
                  className={`${styles['user-table__action']} ${styles['search-filter__addBtn']}`}
                >
                  <button
                    type="button"
                    className={`btn-blue ${styles['user-table__action--button']}`}
                    onClick={openDrawerHandler}
                  >
                    <AddSvg />
                    <p>Add Grocery</p>
                  </button>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default SearchAndFilter;
